import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, XMarkIcon, Cog6ToothIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

const DAYS     = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const HOL_TYPES = [
  { value:'public',    label:'Public Holiday',  color:'#ef4444', bg:'#fef2f2' },
  { value:'school',    label:'School Holiday',   color:'#3b6ef8', bg:'#eef2ff' },
  { value:'emergency', label:'Emergency Closure',color:'#f59e0b', bg:'#fffbeb' },
];
const LEAVE_TYPES = ['sick','casual','emergency','maternity','annual','unpaid','other'];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .hol * { box-sizing:border-box; }
  .hol { font-family:'Sora',sans-serif; color:#1a1d2e; }
  .hol-title { font-size:1.4rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:3px; }
  .hol-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.5rem; }
  /* Page tabs */
  .page-tabs { display:flex; gap:6px; margin-bottom:1.5rem; }
  .page-tab  { display:inline-flex; align-items:center; gap:7px; padding:8px 18px; border-radius:10px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .page-tab svg { width:15px; height:15px; stroke-width:2; }
  .page-tab.active { background:#3b6ef8; color:white; border-color:#3b6ef8; }
  /* Calendar */
  .cal-header { display:flex; align-items:center; gap:1rem; margin-bottom:1.2rem; }
  .cal-nav { width:32px; height:32px; border-radius:8px; border:1.5px solid #e0e4f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:.9rem; color:#6b7194; }
  .cal-nav:hover { border-color:#3b6ef8; color:#3b6ef8; }
  .cal-label { font-size:1rem; font-weight:700; color:#0f1123; min-width:140px; text-align:center; }
  .btn-add-hol { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; margin-left:auto; }
  .btn-add-hol svg { width:14px; height:14px; stroke-width:2.5; }
  /* Calendar grid */
  .cal-grid { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .cal-days-header { display:grid; grid-template-columns:repeat(7,1fr); background:#fafbff; border-bottom:1px solid #edf0f9; }
  .cal-day-hdr { padding:8px; text-align:center; font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .cal-day-hdr.off { color:#ef4444; }
  .cal-cells { display:grid; grid-template-columns:repeat(7,1fr); }
  .cal-cell { min-height:70px; padding:6px; border-right:1px solid #f3f5fb; border-bottom:1px solid #f3f5fb; position:relative; }
  .cal-cell:nth-child(7n) { border-right:none; }
  .cal-cell.off-day { background:#fef9f9; }
  .cal-cell.today   { background:#f0f6ff; }
  .cal-cell.other-month { opacity:.3; }
  .cal-date { font-size:.72rem; font-weight:600; color:#6b7194; margin-bottom:3px; }
  .cal-cell.today .cal-date { color:#3b6ef8; font-weight:700; }
  .hol-chip { font-size:.62rem; font-weight:700; padding:2px 6px; border-radius:5px; margin-top:2px; cursor:pointer; display:block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  /* Holiday list */
  .hol-list { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-top:1.2rem; }
  .hol-list-header { padding:12px 16px; border-bottom:1px solid #edf0f9; font-size:.8rem; font-weight:700; color:#0f1123; display:flex; align-items:center; justify-content:space-between; }
  .hol-row { display:flex; align-items:center; gap:12px; padding:11px 16px; border-bottom:1px solid #f3f5fb; }
  .hol-row:last-child { border-bottom:none; }
  .hol-date-box { width:46px; height:46px; border-radius:10px; display:flex; flex-direction:column; align-items:center; justify-content:center; flex-shrink:0; }
  .hol-date-day  { font-size:1.1rem; font-weight:700; line-height:1; }
  .hol-date-mon  { font-size:.58rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; margin-top:1px; }
  .hol-name  { font-size:.84rem; font-weight:600; color:#0f1123; }
  .hol-meta  { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .type-badge { font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:99px; }
  .paid-badge { font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .unpaid-badge { font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#fef2f2; color:#ef4444; }
  .hol-actions { display:flex; gap:4px; margin-left:auto; }
  .hol-btn { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .hol-btn svg { width:13px; height:13px; stroke-width:2; }
  .hol-btn.del:hover { background:#fef2f2; border-color:#fecaca; color:#ef4444; }
  .hol-btn.edit:hover { background:#eef2ff; border-color:#c7d2fe; color:#3b6ef8; }
  .hol-empty { text-align:center; padding:2rem; color:#9094a8; font-size:.8rem; }
  /* Settings */
  .sett-section { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.4rem; margin-bottom:1.2rem; }
  .sett-title   { font-size:.92rem; font-weight:700; color:#0f1123; margin-bottom:.3rem; }
  .sett-sub     { font-size:.74rem; color:#9094a8; margin-bottom:1.2rem; }
  .day-grid { display:flex; gap:8px; flex-wrap:wrap; }
  .day-btn  { padding:7px 14px; border-radius:9px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .day-btn.off  { background:#fef2f2; color:#ef4444; border-color:#fecaca; }
  .day-btn.half { background:#fffbeb; color:#f59e0b; border-color:#fde68a; }
  .leave-table { width:100%; border-collapse:collapse; }
  .leave-table th { padding:8px 12px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; border-bottom:1px solid #edf0f9; }
  .leave-table td { padding:9px 12px; font-size:.82rem; border-bottom:1px solid #f3f5fb; text-align:center !important; }
  .leave-table tr:last-child td { border-bottom:none; }
  .leave-cards { display:none; padding:0; }
  .leave-card  { background:white; border:1px solid #e8ebf4; border-radius:12px; padding:13px; margin-bottom:8px; }
  .lc-row  { display:flex; justify-content:space-between; align-items:center; padding:4px 0; gap:10px; }
  .lc-row:not(:last-child) { border-bottom:1px solid #f3f5fb; }
  .lc-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; flex-shrink:0; }
  .lc-desc { font-size:.74rem; color:#9094a8; text-align:right; }
  .leave-sel { font-family:'Sora',sans-serif; font-size:.8rem; padding:5px 10px; border:1.5px solid #e0e4f0; border-radius:8px; background:white; outline:none; }
  .leave-sel:focus { border-color:#3b6ef8; }
  .num-input { font-family:'JetBrains Mono',monospace; font-size:.82rem; padding:6px 10px; border:1.5px solid #e0e4f0; border-radius:8px; background:#f8f9fc; outline:none; width:80px; }
  .num-input:focus { border-color:#3b6ef8; background:white; }
  .btn-save-sett { padding:9px 24px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.84rem; font-weight:600; cursor:pointer; }
  .btn-save-sett:disabled { opacity:.6; cursor:not-allowed; }
  /* Modal */
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:460px; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; display:flex; flex-direction:column; gap:12px; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; }
  .ff { display:flex; flex-direction:column; gap:5px; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .fi:focus,.fs:focus { border-color:#3b6ef8; background:white; }
  .btn-save  { padding:9px 20px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-save:disabled { opacity:.6; cursor:not-allowed; }
  .btn-ghost { padding:9px 18px; border:none; border-radius:10px; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .toggle-row { display:flex; align-items:center; gap:10px; }
  .toggle-lbl { font-size:.8rem; color:#6b7194; font-weight:600; }
  input[type=checkbox] { width:16px; height:16px; cursor:pointer; accent-color:#3b6ef8; }
  @media (max-width:768px) {
    .leave-table-wrap { display:none !important; }
    .leave-cards { display:block; }
  }
`;

const fmtDate = d => new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'});

export default function Holidays() {
  const [tab,         setTab]         = useState('calendar');
  const [year,        setYear]        = useState(new Date().getFullYear());
  const [viewMonth,   setViewMonth]   = useState(new Date().getMonth()); // 0-11
  const [holidays,    setHolidays]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [modal,       setModal]       = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [form,        setForm]        = useState({ name:'', date:'', type:'school', isPaid:true, notes:'' });

  // Settings
  const [settings,    setSettings]    = useState({
    weeklyOffDays: [0],
    absentDeductionPerDay: 0, latePenaltyPerMinute: 0, earlyExitPenaltyPerMinute: 0,
    leavePolicy: { sick:'paid', casual:'paid', emergency:'paid', maternity:'paid', annual:'paid', unpaid:'unpaid', other:'unpaid' },
    annualPaidLeaves: 10, annualCasualLeaves: 10, annualSickLeaves: 10,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const loadHolidays = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/holidays?year=${year}`);
      setHolidays(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load holidays'); }
    finally { setLoading(false); }
  };

  // ── Vacation state ──────────────────────────────────────────────────────────
  const [vacations,    setVacations]    = useState([]);
  const [vacModal,     setVacModal]     = useState(false);
  const [vacEdit,      setVacEdit]      = useState(null);
  const [vacSaving,    setVacSaving]    = useState(false);
  const [vacForm,      setVacForm]      = useState({ name:'', startDate:'', endDate:'', type:'summer', salaryRule:'proportional', notes:'' });

  const loadVacations = async () => {
    try {
      const { data } = await api.get(`/admin/vacations?year=${year}`);
      setVacations(Array.isArray(data) ? data : []);
    } catch {}
  };

  const handleSaveVacation = async () => {
    if (!vacForm.name || !vacForm.startDate || !vacForm.endDate)
      return toast.error('Name, start and end date are required');
    if (new Date(vacForm.startDate) > new Date(vacForm.endDate))
      return toast.error('Start date must be before end date');
    setVacSaving(true);
    try {
      if (vacEdit) {
        await api.put(`/admin/vacations/${vacEdit._id}`, vacForm);
        toast.success('Vacation period updated');
      } else {
        await api.post('/admin/vacations', vacForm);
        toast.success('Vacation period added');
      }
      setVacModal(false);
      setVacEdit(null);
      setVacForm({ name:'', startDate:'', endDate:'', type:'summer', salaryRule:'proportional', notes:'' });
      loadVacations();
    } catch { toast.error('Failed to save'); }
    finally { setVacSaving(false); }
  };

  const openEditVacation = (v) => {
    setVacEdit(v);
    setVacForm({
      name: v.name, type: v.type, salaryRule: v.salaryRule, notes: v.notes || '',
      startDate: new Date(v.startDate).toISOString().slice(0,10),
      endDate:   new Date(v.endDate).toISOString().slice(0,10),
    });
    setVacModal(true);
  };

  const getDaysBetween = (s, e) => {
    const ms = new Date(e) - new Date(s);
    return Math.round(ms / 86400000) + 1;
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get('/admin/holidays/settings');
      setSettings(prev => ({ ...prev, ...data }));
    } catch {}
  };

  useEffect(() => { loadHolidays(); loadVacations(); }, [year]);
  useEffect(() => { loadSettings(); }, []);

  // Calendar helpers
  const today    = new Date();
  const firstDay = new Date(year, viewMonth, 1);
  const lastDay  = new Date(year, viewMonth + 1, 0);
  const startPad = firstDay.getDay(); // 0=Sun

  const cells = [];
  // Prev month padding
  for (let i = 0; i < startPad; i++) {
    const d = new Date(year, viewMonth, -startPad + i + 1);
    cells.push({ date: d, isCurrentMonth: false });
  }
  // Current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push({ date: new Date(year, viewMonth, d), isCurrentMonth: true });
  }
  // Next month padding
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ date: new Date(year, viewMonth + 1, d), isCurrentMonth: false });
  }

  const getHolidaysForDate = (date) => {
    return holidays.filter(h => {
      const hd = new Date(h.date);
      return hd.getFullYear() === date.getFullYear() &&
             hd.getMonth() === date.getMonth() &&
             hd.getDate() === date.getDate();
    });
  };

  const isOffDay = (date) => settings.weeklyOffDays?.includes(date.getDay());

  const handleSave = async () => {
    if (!form.name || !form.date) return toast.error('Name and date are required');
    setSaving(true);
    try {
      if (editTarget) {
        await api.put(`/admin/holidays/${editTarget._id}`, form);
        toast.success('Holiday updated');
      } else {
        await api.post('/admin/holidays', form);
        toast.success('Holiday added — staff attendance auto-marked');
      }
      setModal(false);
      setEditTarget(null);
      setForm({ name:'', date:'', type:'school', isPaid:true, notes:'' });
      loadHolidays();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const openEdit = (h) => {
    setEditTarget(h);
    setForm({ name:h.name, date:new Date(h.date).toISOString().slice(0,10), type:h.type, isPaid:h.isPaid, notes:h.notes||'' });
    setModal(true);
  };

  const toggleOffDay = (dayIdx) => {
    if (dayIdx === 6) return; // Saturday controlled by saturdayPolicy
    setSettings(p => {
      const off = [...(p.weeklyOffDays||[])].filter(d => d !== 6);
      const i   = off.indexOf(dayIdx);
      if (i >= 0) off.splice(i, 1);
      else        off.push(dayIdx);
      return { ...p, weeklyOffDays: off.sort() };
    });
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/holidays/settings', settings);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSavingSettings(false); }
  };

  const monthHolidays = holidays.filter(h => new Date(h.date).getMonth() === viewMonth);


  const markWeeklyOffs = async () => {
    const month = `${year}-${String(viewMonth+1).padStart(2,'0')}`;
    try {
      const { data } = await api.post('/admin/holidays/mark-weekly-offs', { month });
      toast.success(data.message || 'Weekly off-days marked');
    } catch { toast.error('Failed to mark weekly offs'); }
  };


  const VAC_TYPES = {
    summer: { label:'Summer Vacation',  color:'#f59e0b', bg:'#fffbeb' },
    winter: { label:'Winter Vacation',  color:'#3b6ef8', bg:'#eef2ff' },
    spring: { label:'Spring Break',     color:'#10c98e', bg:'#ecfdf5' },
    other:  { label:'Other Vacation',   color:'#9094a8', bg:'#f0f2f7' },
  };
  const SALARY_RULES = {
    none:         { label:'No Salary',          color:'#ef4444', desc:'Staff not paid during vacation' },
    proportional: { label:'Proportional Salary', color:'#f59e0b', desc:'Paid only for days before/after vacation (÷30)' },
    full:         { label:'Full Salary',         color:'#10c98e', desc:'Full monthly salary paid despite vacation' },
  };
  return (
    <div className="hol"><style>{css}</style>
      <div className="hol-title">Holidays & Leave Policy</div>
      <div className="hol-sub">Manage public holidays, school off-days, weekly schedule and leave rules.</div>

      <div className="page-tabs">
        <button className={`page-tab ${tab==='calendar'?'active':''}`} onClick={() => setTab('calendar')}>
          <CalendarDaysIcon />Calendar
        </button>
        <button className={`page-tab ${tab==='vacations'?'active':''}`} onClick={() => setTab('vacations')}>
          <svg style={{width:15,height:15,strokeWidth:2,stroke:'currentColor',fill:'none'}} viewBox="0 0 24 24"><path d="M3 9h18M9 3v18M15 3v18" strokeLinecap="round"/></svg>
          Vacations
        </button>
        <button className={`page-tab ${tab==='settings'?'active':''}`} onClick={() => setTab('settings')}>
          <Cog6ToothIcon />Settings
        </button>
      </div>

      {tab === 'calendar' ? (
        <>
          {/* Calendar nav */}
          <div className="cal-header">
            <button className="cal-nav" onClick={() => { if (viewMonth===0){setViewMonth(11);setYear(y=>y-1);}else setViewMonth(m=>m-1); }}>←</button>
            <div className="cal-label">{MONTHS[viewMonth]} {year}</div>
            <button className="cal-nav" onClick={() => { if (viewMonth===11){setViewMonth(0);setYear(y=>y+1);}else setViewMonth(m=>m+1); }}>→</button>
            <button className="cal-nav" onClick={() => {setYear(today.getFullYear());setViewMonth(today.getMonth());}}>Today</button>
            <button onClick={markWeeklyOffs}
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 14px',border:'1.5px solid #e0e4f0',borderRadius:10,background:'white',fontFamily:'Sora',fontSize:'.8rem',fontWeight:600,cursor:'pointer',color:'#6b7194'}}>
              📅 Mark Off-Days
            </button>
            <button className="btn-add-hol" onClick={() => { setEditTarget(null); setForm({ name:'', date:`${year}-${String(viewMonth+1).padStart(2,'0')}-01`, type:'school', isPaid:true, notes:'' }); setModal(true); }}>
              <PlusIcon />Add Holiday
            </button>
          </div>

          {/* Calendar grid */}
          <div className="cal-grid">
            <div className="cal-days-header">
              {DAYS.map((d,i) => (
                <div key={d} className={`cal-day-hdr ${settings.weeklyOffDays?.includes(i)?'off':''}`}>{d}</div>
              ))}
            </div>
            <div className="cal-cells">
              {cells.map((cell, idx) => {
                const cellHols = getHolidaysForDate(cell.date);
                const isOff    = isOffDay(cell.date);
                const isToday  = cell.date.toDateString() === today.toDateString();
                return (
                  <div key={idx} className={`cal-cell${!cell.isCurrentMonth?' other-month':''}${isOff?' off-day':''}${isToday?' today':''}`}>
                    <div className="cal-date">{cell.date.getDate()}</div>
                    {isOff && cell.isCurrentMonth && <div style={{fontSize:'.58rem',color:'#ef4444',fontWeight:600}}>Off</div>}
                    {cellHols.map(h => {
                      const t = HOL_TYPES.find(x => x.value === h.type) || HOL_TYPES[0];
                      return (
                        <div key={h._id} className="hol-chip"
                          style={{background:t.bg, color:t.color}}
                          onClick={() => openEdit(h)}
                          title={h.name}>
                          {h.name}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Holiday list for current month */}
          <div className="hol-list">
            <div className="hol-list-header">
              <span>{MONTHS[viewMonth]} {year} — {monthHolidays.length} holiday{monthHolidays.length!==1?'s':''}</span>
            </div>
            {monthHolidays.length === 0 ? (
              <div className="hol-empty">No holidays this month.</div>
            ) : monthHolidays.map(h => {
              const d  = new Date(h.date);
              const t  = HOL_TYPES.find(x => x.value === h.type) || HOL_TYPES[0];
              return (
                <div key={h._id} className="hol-row">
                  <div className="hol-date-box" style={{background:t.bg,color:t.color}}>
                    <div className="hol-date-day">{d.getDate()}</div>
                    <div className="hol-date-mon">{MONTHS[d.getMonth()]}</div>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div className="hol-name">{h.name}</div>
                    <div className="hol-meta">{DAYS[d.getDay()]} · {h.notes||'No notes'}</div>
                  </div>
                  <span className="type-badge" style={{background:t.bg,color:t.color}}>{t.label}</span>
                  <span className={h.isPaid?'paid-badge':'unpaid-badge'}>{h.isPaid?'Paid':'Unpaid'}</span>
                  <div className="hol-actions">
                    <button className="hol-btn edit" onClick={() => openEdit(h)} title="Edit"><PencilIcon /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : tab === 'vacations' ? (
        <>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1.2rem'}}>
            <div>
              <div style={{fontSize:'.92rem',fontWeight:700,color:'#0f1123'}}>Vacation Periods</div>
              <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:2}}>Summer/winter/spring breaks. Salary = days worked × (monthlySalary ÷ 30).</div>
            </div>
            <button className="btn-add-hol" onClick={()=>{setVacEdit(null);setVacForm({name:'',startDate:'',endDate:'',type:'summer',salaryRule:'proportional',notes:''});setVacModal(true);}}>
              <PlusIcon />Add Vacation Period
            </button>
          </div>

          {vacations.length === 0 ? (
            <div style={{background:'white',borderRadius:16,border:'1px solid #e8ebf4',padding:'3rem',textAlign:'center',color:'#9094a8',fontSize:'.82rem'}}>No vacation periods added yet.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {vacations.map(v => {
                const VAC = {summer:{label:'Summer',color:'#f59e0b',bg:'#fffbeb',icon:'☀️'},winter:{label:'Winter',color:'#3b6ef8',bg:'#eef2ff',icon:'❄️'},spring:{label:'Spring',color:'#10c98e',bg:'#ecfdf5',icon:'🌸'},other:{label:'Other',color:'#9094a8',bg:'#f0f2f7',icon:'📅'}};
                const SAL = {none:{label:'No Salary',color:'#ef4444'},proportional:{label:'Proportional',color:'#f59e0b'},full:{label:'Full Salary',color:'#10c98e'}};
                const vt   = VAC[v.type]||VAC.other;
                const sr   = SAL[v.salaryRule]||SAL.proportional;
                const days = Math.round((new Date(v.endDate)-new Date(v.startDate))/86400000)+1;
                return (
                  <div key={v._id} style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'1.1rem 1.3rem',display:'flex',alignItems:'center',gap:14}}>
                    <div style={{width:46,height:46,borderRadius:12,background:vt.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.3rem',flexShrink:0}}>{vt.icon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{v.name}</div>
                      <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:2}}>
                        {new Date(v.startDate).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'})} → {new Date(v.endDate).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'})} · <strong>{days} days</strong>
                      </div>
                      {v.notes && <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:2}}>{v.notes}</div>}
                    </div>
                    <span style={{fontSize:'.72rem',fontWeight:700,padding:'3px 10px',borderRadius:99,background:vt.bg,color:vt.color}}>{vt.label}</span>
                    <span style={{fontSize:'.72rem',fontWeight:700,color:sr.color}}>{sr.label}</span>
                    <div style={{display:'flex',gap:5}}>
                      <button className="hol-btn edit" onClick={()=>openEditVacation(v)} title="Edit"><PencilIcon /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {vacModal && createPortal(
            <div className="mb" onClick={()=>setVacModal(false)}>
              <div className="mo" onClick={e=>e.stopPropagation()}>
                <div className="mo-hd">
                  <div className="mo-title">{vacEdit?'Edit Vacation Period':'Add Vacation Period'}</div>
                  <button className="mo-x" onClick={()=>setVacModal(false)}><XMarkIcon /></button>
                </div>
                <div className="mo-bd">
                  <div className="ff">
                    <label className="fl">Name *</label>
                    <input className="fi" value={vacForm.name} onChange={e=>setVacForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Summer Vacations 2024" />
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                    <div className="ff">
                      <label className="fl">Start Date *</label>
                      <input type="date" className="fi" value={vacForm.startDate} onChange={e=>setVacForm(p=>({...p,startDate:e.target.value}))} />
                    </div>
                    <div className="ff">
                      <label className="fl">End Date *</label>
                      <input type="date" className="fi" value={vacForm.endDate} onChange={e=>setVacForm(p=>({...p,endDate:e.target.value}))} />
                    </div>
                  </div>
                  {vacForm.startDate && vacForm.endDate && new Date(vacForm.startDate) <= new Date(vacForm.endDate) && (
                    <div style={{background:'#f0f2f7',borderRadius:8,padding:'8px 12px',fontSize:'.76rem',color:'#6b7194'}}>
                      📅 <strong>{Math.round((new Date(vacForm.endDate)-new Date(vacForm.startDate))/86400000)+1} days</strong> vacation period
                    </div>
                  )}
                  <div className="ff">
                    <label className="fl">Type</label>
                    <select className="fs" value={vacForm.type} onChange={e=>setVacForm(p=>({...p,type:e.target.value}))}>
                      <option value="summer">Summer Vacation</option>
                      <option value="winter">Winter Vacation</option>
                      <option value="spring">Spring Break</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="ff">
                    <label className="fl">Salary Rule *</label>
                    <select className="fs" value={vacForm.salaryRule} onChange={e=>setVacForm(p=>({...p,salaryRule:e.target.value}))}>
                      <option value="proportional">Proportional — paid for working days only (÷30)</option>
                      <option value="none">No Salary — staff unpaid during vacation</option>
                      <option value="full">Full Salary — full pay despite vacation</option>
                    </select>
                  </div>
                  {vacForm.salaryRule !== 'full' && vacForm.startDate && vacForm.endDate && (
                    <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:9,padding:'9px 12px',fontSize:'.76rem',color:'#92400e'}}>
                      ⚠️ Salary = days worked × (monthlySalary ÷ 30)
                    </div>
                  )}
                  <div className="ff">
                    <label className="fl">Notes</label>
                    <input className="fi" value={vacForm.notes} onChange={e=>setVacForm(p=>({...p,notes:e.target.value}))} placeholder="Optional notes" />
                  </div>
                </div>
                <div className="mo-ft">
                  <button className="btn-ghost" onClick={()=>setVacModal(false)}>Cancel</button>
                  <button className="btn-save" onClick={handleSaveVacation} disabled={vacSaving}>
                    {vacSaving ? 'Saving…' : vacEdit ? 'Update' : 'Add Period'}
                  </button>
                </div>
              </div>
            </div>
          
      , document.body)}
        </>

      ) : (
        /* Settings tab */
        <>
          {/* Weekly off-days */}
          <div className="sett-section">
            <div className="sett-title">Weekly Off-Days</div>
            <div className="sett-sub">Select which days of the week are off. These days are excluded from working day calculations and salary.</div>
            <div className="day-grid">
              {/* Saturday (index 6) controlled by Saturday Policy */}
              {DAYS.map((d, i) => i === 6 ? null : (
                <button key={d} className={`day-btn ${settings.weeklyOffDays?.includes(i)?'off':''}`}
                  onClick={() => toggleOffDay(i)}>
                  {d} {settings.weeklyOffDays?.includes(i) ? '✗' : '✓'}
                </button>
              ))}
            </div>
            <div style={{fontSize:'.74rem',color:'#9094a8',marginTop:10}}>
              Currently off: {settings.weeklyOffDays?.map(i=>DAYS[i]).join(', ') || 'None'}
            </div>
          </div>

          {/* Salary deduction rules */}
          <div className="sett-section">
            <div className="sett-title">Salary Deduction Rules</div>
            <div className="sett-sub">These apply to all staff when calculating monthly salary.</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16}}>
              {[
                { key:'absentDeductionPerDay',     label:'Absent Deduction / Day (Rs.)' },
                { key:'latePenaltyPerMinute',       label:'Late Penalty / Minute (Rs.)' },
                { key:'earlyExitPenaltyPerMinute',  label:'Early Exit Penalty / Min (Rs.)' },
              ].map(f => (
                <div key={f.key} className="ff">
                  <label className="fl">{f.label}</label>
                  <input type="number" min="0" className="num-input" style={{width:'100%'}}
                    value={settings[f.key]||0}
                    onChange={e => setSettings(p => ({...p,[f.key]:Number(e.target.value)}))} />
                </div>
              ))}
            </div>
          </div>

          {/* Leave policy */}
          <div className="sett-section">
            <div className="sett-title">Leave Policy</div>
            <div className="sett-sub">Define whether each leave type is paid, unpaid, or half-paid. This affects salary calculation for all staff.</div>
            <div className="leave-table-wrap">
              <table className="leave-table">
                <thead><tr><th>Leave Type</th><th>Salary Impact</th><th>Description</th></tr></thead>
                <tbody>
                  {LEAVE_TYPES.map(lt => (
                    <tr key={lt}>
                      <td style={{fontWeight:600,textTransform:'capitalize'}}>{lt}</td>
                      <td>
                        <select className="leave-sel"
                          value={settings.leavePolicy?.[lt] || 'unpaid'}
                          onChange={e => setSettings(p => ({
                            ...p,
                            leavePolicy: { ...p.leavePolicy, [lt]: e.target.value }
                          }))}>
                          <option value="paid">Paid — full salary</option>
                          <option value="half">Half-paid — 50% deduction</option>
                          <option value="unpaid">Unpaid — full deduction</option>
                        </select>
                      </td>
                      <td style={{fontSize:'.74rem',color:'#9094a8'}}>
                        {settings.leavePolicy?.[lt]==='paid' ? 'No salary cut for this leave type'
                         : settings.leavePolicy?.[lt]==='half' ? 'Half day deducted from salary'
                         : 'Full day deducted from salary'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="leave-cards">
              {LEAVE_TYPES.map(lt => (
                <div key={lt} className="leave-card">
                  <div className="lc-row">
                    <span className="lc-label">Leave Type</span>
                    <span style={{fontWeight:600,textTransform:'capitalize'}}>{lt}</span>
                  </div>
                  <div className="lc-row">
                    <span className="lc-label">Salary Impact</span>
                    <select className="leave-sel"
                      value={settings.leavePolicy?.[lt] || 'unpaid'}
                      onChange={e => setSettings(p => ({
                        ...p,
                        leavePolicy: { ...p.leavePolicy, [lt]: e.target.value }
                      }))}>
                      <option value="paid">Paid — full salary</option>
                      <option value="half">Half-paid — 50% deduction</option>
                      <option value="unpaid">Unpaid — full deduction</option>
                    </select>
                  </div>
                  <div className="lc-row">
                    <span className="lc-label">Description</span>
                    <span className="lc-desc">
                      {settings.leavePolicy?.[lt]==='paid' ? 'No salary cut'
                       : settings.leavePolicy?.[lt]==='half' ? 'Half day deducted'
                       : 'Full day deducted'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Annual quotas */}
          <div className="sett-section">
            <div className="sett-title">Annual Leave Quota</div>
            <div className="sett-sub">Maximum paid leaves allowed per year per staff member.</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16}}>
              {[
                { key:'annualPaidLeaves',   label:'Paid / Annual Leaves' },
                { key:'annualCasualLeaves', label:'Casual Leaves' },
                { key:'annualSickLeaves',   label:'Sick Leaves' },
              ].map(f => (
                <div key={f.key} className="ff">
                  <label className="fl">{f.label} / Year</label>
                  <input type="number" min="0" className="num-input" style={{width:'100%'}}
                    value={settings[f.key]||0}
                    onChange={e => setSettings(p => ({...p,[f.key]:Number(e.target.value)}))} />
                </div>
              ))}
            </div>
          </div>

          <button className="btn-save-sett" disabled={savingSettings} onClick={saveSettings}>
            {savingSettings ? 'Saving…' : 'Save All Settings'}
          </button>
        </>
      )}

      {/* Add/Edit Holiday Modal */}
      {modal && createPortal(
        <div className="mb" onClick={() => setModal(false)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">{editTarget ? 'Edit Holiday' : 'Add Holiday'}</div>
              <button className="mo-x" onClick={() => setModal(false)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div className="ff">
                <label className="fl">Holiday Name *</label>
                <input className="fi" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Eid ul Fitr, Independence Day" />
              </div>
              <div className="ff">
                <label className="fl">Date *</label>
                <input type="date" className="fi" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} />
              </div>
              <div className="ff">
                <label className="fl">Type</label>
                <select className="fs" value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}>
                  {HOL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="toggle-row">
                <input type="checkbox" id="isPaid" checked={form.isPaid} onChange={e=>setForm(p=>({...p,isPaid:e.target.checked}))} />
                <label htmlFor="isPaid" className="toggle-lbl">Paid Holiday — staff salary not deducted, attendance auto-marked as holiday</label>
              </div>
              <div className="ff">
                <label className="fl">Notes (optional)</label>
                <input className="fi" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Any notes…" />
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>{saving?'Saving…':editTarget?'Update':'Add Holiday'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}